import type { EmployeeStatus, EmploymentType } from './constants';

/** Mirrors EmployeeReadSerializer (apps/hrm/serializers.py). */
export interface Employee {
  id: number;
  /** Opaque UUIDv7 the SPA links by, so int PKs stay out of URLs (ADR-041). */
  public_id: string;
  user: number | null;
  /** Email of the linked Django auth account; null when not yet invited. */
  user_email: string | null;
  /** Photo URL served by the backend; null if unset. */
  photo: string | null;
  first_name: string;
  last_name: string;
  /** Short code used in staff-invoice numbers (e.g. "HRD"). */
  code: string;
  email: string;
  /** HR-managed contact details (separate from the linked User's own profile). */
  personal_email: string;
  phone_number: string;
  city: string;
  address: string;
  /** Contractor-invoice identity (FOP/PE) — legal designation incl. patronymic. */
  legal_name: string;
  legal_name_local: string;
  address_local: string;
  /** Services contract the staff invoice bills under. */
  contract_number: string;
  contract_date: string | null;
  employment_type: EmploymentType;
  job_title: string;
  department: number | null;
  /** Resolved department name for display/grouping (read-only). */
  department_name: string | null;
  manager: number | null;
  hire_date: string | null;
  /** End of the probation/trial period (record-only). */
  trial_period_end: string | null;
  end_date: string | null;
  status: EmployeeStatus;
  /** ISO 3166-1 alpha-2 code. */
  country: string;
  /** Opt-in gate (ADR-046): commission only accrues on this person's deals when
   *  true. Default false — salaried lead-gen / SDRs / ops never accrue. */
  commission_eligible: boolean;
  /** Default sales commission rate (%) seeding new deals this person owns.
   *  Compensation data — `null` when masked for roles outside SALARY_VISIBLE_ROLES (C7). */
  default_commission_pct: string | null;
  /** True when the linked member holds a sales role — gates commission UI. */
  is_sales: boolean;
  created: string;
  modified: string;
}

/** Write payload — EmployeeWriteSerializer. `user` links a Django account and is
 *  only set by HR-manage roles (Super Admin / Founder / HR). */
export interface EmployeeInput {
  user?: number | null;
  first_name: string;
  last_name: string;
  code?: string;
  email: string;
  personal_email: string;
  phone_number: string;
  city: string;
  address: string;
  /** Contractor-invoice identity (FOP/PE) — legal designation incl. patronymic. */
  legal_name: string;
  legal_name_local: string;
  address_local: string;
  /** Services contract the staff invoice bills under. */
  contract_number: string;
  contract_date: string | null;
  employment_type: EmploymentType;
  job_title: string;
  department: number | null;
  manager: number | null;
  hire_date: string | null;
  trial_period_end: string | null;
  end_date: string | null;
  status: EmployeeStatus;
  country: string;
  /** Only writable by compensation roles (Founder/Super Admin/Accountant);
   *  the backend drops these from the write payload for everyone else. */
  commission_eligible?: boolean;
  default_commission_pct?: string | null;
}

/** Org unit (mirrors DepartmentReadSerializer). */
export interface Department {
  id: number;
  name: string;
  /** Short code used in the department part of employee codes (e.g. "QA"). */
  code: string;
  description: string;
}

export interface DepartmentInput {
  name: string;
  code?: string;
  description?: string;
}

/**
 * Mirrors SalaryRecordReadSerializer. Money is a value object; amount/currency/
 * fx_rate_to_base/base_amount are `null` when masked server-side for roles
 * outside SALARY_VISIBLE_ROLES (C7).
 */
export interface SalaryRecord {
  id: number;
  /** Opaque UUIDv7 (ADR-041). */
  public_id: string;
  employee: number;
  effective_date: string;
  note: string;
  amount: string | null;
  currency: string | null;
  fx_rate_to_base: string | null;
  base_amount: string | null;
  created: string;
  modified: string;
}

/** Salary actually paid for a payroll period (mirrors PayslipReadSerializer).
 * Money fields are `null` when masked for the role (C7). */
export interface Payslip {
  id: number;
  /** Opaque UUIDv7 (ADR-041). */
  public_id: string;
  period_start: string | null;
  period_end: string | null;
  currency: string | null;
  gross: string | null;
  deductions: string | null;
  employer_cost: string | null;
  net: string | null;
  base_net: string | null;
}

export const employeeName = (
  e: Pick<Employee, 'first_name' | 'last_name' | 'email'>,
): string => `${e.first_name} ${e.last_name}`.trim() || e.email || 'Employee';
